import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type CustomerQuote, type FixingIndex, getSymbolFromFixingIndex, NumericField } from '@talos/kyoko';
import { getAllFixingIndicesFromCustomerQuotes } from './utils';

export interface Fixing {
  /** The full FixingIndex object represented by this fixing entry */
  fixingIndex: FixingIndex;
  /**
   * The symbol this FixingIndex is for. Eg BRRNY is the fixing index for BTC-USD.
   * Resolved from the fixing index itself; some index rows may not have enough info for symbol resolution.
   */
  symbol: string | undefined;
  field: NumericField;
}

export interface FixingState {
  fixings: Fixing[];
}

export const EMPTY_FIXING_STATE: FixingState = {
  fixings: [],
};

export interface InitFixingStateParams {
  fixingIndices: FixingIndex[];
}

function createFixingEntry(fixingIndex: FixingIndex): Fixing {
  return {
    fixingIndex,
    symbol: getSymbolFromFixingIndex(fixingIndex),
    field: new NumericField({ isRequired: true, minimum: '0' }),
  };
}

export function getInitialFixingState({ fixingIndices }: InitFixingStateParams): FixingState {
  return {
    fixings: fixingIndices.map(createFixingEntry),
  };
}

// The reducer here is pretty trivial for now, but it will grow in the future
const fixingSlice = createSlice({
  name: 'fixingSlice',
  initialState: getInitialFixingState({ fixingIndices: [] }),
  reducers: {
    updateFixingEntries(state, action: PayloadAction<{ fixingIndices: FixingIndex[] }>) {
      // We should either add or remove entries in our fixing state based on the new selections
      const newIndicesSet = new Set(action.payload.fixingIndices.map(fi => fi.Name));
      const existingIndicesSet = new Set(state.fixings.map(f => f.fixingIndex.Name));

      // Remove any existing entries that aren't in the newly selected indices
      const newFixings = state.fixings.filter(fixing => newIndicesSet.has(fixing.fixingIndex.Name));

      // Then figure out which indices are new and to be added, then add them
      const indicesToAdd = action.payload.fixingIndices.filter(
        fixingIndex => !existingIndicesSet.has(fixingIndex.Name)
      );
      indicesToAdd.forEach(fixingIndex => newFixings.push(createFixingEntry(fixingIndex)));

      state.fixings = newFixings;
    },
    // Set the fixing value (number) for some leg (given the leg symbol)
    setFixing(state, action: PayloadAction<{ fixingIndex: string; value: string }>) {
      state.fixings = state.fixings.map(fixing => {
        if (fixing.fixingIndex.Name !== action.payload.fixingIndex) {
          return fixing; // unchanged
        }

        return { ...fixing, field: fixing.field.updateValue(action.payload.value).validate() };
      });
    },
    touchAll(state) {
      state.fixings = state.fixings.map(fixing => ({ ...fixing, field: fixing.field.setTouched(true) }));
    },
  },
});

export function canApplyFixing(
  state: FixingState,
  customerQuotesToFix: CustomerQuote[],
  fixingIndicesByName: Map<string, FixingIndex>
): { valid: true } | { valid: false; reason: string } {
  if (!state.fixings || state.fixings.length === 0) {
    return { valid: false, reason: 'No fixing index selected' };
  }

  if (state.fixings.some(fixing => fixing.field.hasInvalidValue)) {
    return { valid: false, reason: 'Invalid fixing prices provided' };
  }

  // All fixingIndices found in the list of CQs set to be fixed must be represented in the state.fixings array
  const providedIndices = new Set(state.fixings.map(f => f.fixingIndex.Name));
  const requiredIndices = [...getAllFixingIndicesFromCustomerQuotes(customerQuotesToFix)];
  const missingIndices = requiredIndices.filter(requiredIndex => !providedIndices.has(requiredIndex));
  if (missingIndices.length > 0) {
    const missingIndexLabels = missingIndices.map(index => fixingIndicesByName.get(index)?.DisplayName ?? index);
    const singular = missingIndexLabels.length === 1;
    return {
      valid: false,
      reason: `${singular ? 'Missing index' : 'Missing indices'}: ${missingIndexLabels.join(', ')}`,
    };
  }

  return { valid: true };
}

export const fixingSliceActions = { ...fixingSlice.actions };
export const fixingSliceReducer = fixingSlice.reducer;
